import { useEffect, useRef, useState } from 'react';
import { Activity } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardBody } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Chart } from '@vnetwork/ui/charts';
import { useMonitorStatus } from '@/api/media/live-live-streams';
import { env } from '@/config/env';
import { streamStatusTone } from '../live-stream-status';
import styles from './JobMonitorPanel.module.css';

interface Props {
  serviceId: string;
  streamId: string;
}

interface MonitorPoint {
  timestamp: string;
  type: string;
  status: string;
  fps?: number;
  bitrate?: number;
}

type WsStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

function wsStatusTone(s: WsStatus): 'info' | 'success' | 'neutral' | 'danger' {
  if (s === 'connecting') return 'info';
  if (s === 'connected')  return 'success';
  if (s === 'error')      return 'danger';
  return 'neutral';
}

function wsStatusLabel(s: WsStatus): string {
  if (s === 'connecting')   return 'Connecting…';
  if (s === 'connected')    return 'Connected';
  if (s === 'disconnected') return 'Disconnected';
  return 'Error';
}

/** Parse server timestamp "YYYY-MM-DD HH:mm:ss.SSS" (UTC) → "HH:MM:SS" local. */
function fmtTimestamp(raw: string): string {
  const d = new Date(raw.replace(' ', 'T') + 'Z');
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

const MAX_POINTS = 100;

export function JobMonitorPanel({ serviceId, streamId }: Props) {
  const [points, setPoints] = useState<MonitorPoint[]>([]);
  const [wsStatus, setWsStatus] = useState<WsStatus>('connecting');
  const [streamStatus, setStreamStatus] = useState<string | null>(null);
  const [wsError, setWsError] = useState<string | null>(null);
  const [wsTerminated, setWsTerminated] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  // Pending deferred-close timer — lets a StrictMode remount reuse the socket
  // instead of opening a second connection.
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // HTTP polling fallback — only active when the WS has errored or been terminated.
  const { data: pollData, isError: pollError } = useMonitorStatus(
    serviceId,
    streamId,
    { enabled: wsTerminated && !!streamId },
  );

  useEffect(() => {
    if (!streamId) return;

    const wsBase = env.serviceApiV3Url.replace(/^http/, 'ws');
    const url = `${wsBase}/live-streams/${streamId}/monitor/ws`;

    // A StrictMode remount (or effect re-run) cleans up then immediately re-runs.
    // Cancel any deferred close and REUSE the existing socket for the same URL so
    // we don't open a duplicate connection. Only open a new socket when there's
    // none, the URL changed, or the previous one is closing/closed.
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    // `active` guards against a superseded/closing socket mutating state after
    // teardown (its late onerror/onclose would otherwise show a false error).
    let active = true;

    let ws = wsRef.current;
    if (!ws || ws.url !== url || ws.readyState > WebSocket.OPEN) {
      setWsStatus('connecting');
      setWsError(null);
      ws = new WebSocket(url);
      wsRef.current = ws;
    } else if (ws.readyState === WebSocket.OPEN) {
      // Reused an already-open socket — reflect its live state immediately.
      setWsStatus('connected');
    }

    ws.onopen = () => { if (active) setWsStatus('connected'); };

    ws.onmessage = (ev) => {
      if (!active) return;
      try {
        const msg = JSON.parse(ev.data as string) as MonitorPoint & { error?: string };
        // BE sends {type:'error', error:'...'} on terminal stream errors.
        // Surface the message and stop appending zero-value chart rows.
        if (msg.type === 'error') {
          setWsError(msg.error ?? 'Stream error reported by provider.');
          return;
        }
        // Skip frames that carry no metric (heartbeats, connection acks) — there
        // is nothing to plot for them.
        if (msg.fps === undefined && msg.bitrate === undefined) return;
        if (msg.status) setStreamStatus(msg.status);
        setPoints((prev) => {
          const next = [...prev, msg];
          return next.length > MAX_POINTS ? next.slice(next.length - MAX_POINTS) : next;
        });
      } catch {
        // malformed frame — ignore
      }
    };

    ws.onerror = () => {
      if (!active) return;
      setWsStatus('error');
      setWsError('WebSocket connection failed. The stream may not be active.');
      setWsTerminated(true);
    };

    ws.onclose = () => {
      if (!active) return;
      setWsStatus((s) => s === 'error' ? s : 'disconnected');
      setWsTerminated(true);
    };

    return () => {
      active = false;
      // Defer the close so a StrictMode remount (which runs synchronously right
      // after this cleanup) can cancel it and reuse the socket. A real unmount
      // has no follow-up run, so the timer fires and the socket closes.
      closeTimerRef.current = setTimeout(() => {
        wsRef.current?.close();
        wsRef.current = null;
        closeTimerRef.current = null;
      }, 0);
    };
  }, [streamId]);

  // Append polling-fallback events into the SAME accumulating series (deduped by
  // timestamp) instead of swapping the chart's data source — the graph keeps
  // growing continuously rather than re-initialising when WS drops to polling.
  useEffect(() => {
    if (!wsTerminated || !pollData) return;
    const incoming: MonitorPoint[] = pollData.events?.length
      ? pollData.events.map((e) => ({ timestamp: e.timestamp ?? '', type: e.type, status: e.status ?? '', fps: e.fps, bitrate: e.bitrate }))
      : pollData.latest
        ? [{ timestamp: pollData.latest.timestamp ?? '', type: pollData.latest.type, status: pollData.latest.status ?? '', fps: pollData.latest.fps, bitrate: pollData.latest.bitrate }]
        : [];
    if (!incoming.length) return;
    setPoints((prev) => {
      const seen = new Set(prev.map((p) => p.timestamp));
      const fresh = incoming.filter((p) => p.timestamp && !seen.has(p.timestamp));
      if (!fresh.length) return prev;
      const next = [...prev, ...fresh];
      return next.length > MAX_POINTS ? next.slice(next.length - MAX_POINTS) : next;
    });
  }, [wsTerminated, pollData]);

  // Build chart data — one row per accumulated data point, keyed by timestamp.
  const chartData = points.map((p) => ({
    ts: p.timestamp ? fmtTimestamp(p.timestamp) : '',
    fps: p.fps ?? 0,
    bitrate: p.bitrate ?? 0,
  }));

  const hasData = chartData.length > 0;

  return (
    <div>
      <div className={styles.statusPills}>
        {streamStatus && (
          <StatusPill tone={streamStatusTone(streamStatus)}>
            {streamStatus.toUpperCase()}
          </StatusPill>
        )}
        <StatusPill tone={wsStatusTone(wsStatus)}>
          {wsStatusLabel(wsStatus)}
        </StatusPill>
      </div>

      {wsError && (
        <Alert tone="danger" title="Connection error" style={{ marginBottom: 'var(--vn-space-4)' }}>
          {wsError} Falling back to status polling.
        </Alert>
      )}

      {pollError && wsTerminated && (
        <Alert tone="warning" title="Monitor data unavailable" style={{ marginBottom: 'var(--vn-space-4)' }}>
          Could not retrieve monitor data via polling. The stream may have ended.
        </Alert>
      )}

      <div className={styles.chartsGrid}>
        <Card flat>
          <CardHeader>
            <CardTitle>Bitrate</CardTitle>
          </CardHeader>
          <CardBody>
            <p className={styles.chartDesc}>Real-time stream bitrate (Kbps)</p>
            {hasData ? (
              <div className={styles.chartWrap}>
                <Chart
                  type="area"
                  data={chartData}
                  xKey="ts"
                  height={260}
                  series={[{ key: 'bitrate', label: 'Bitrate (Kbps)' }]}
                  showGrid
                  showXAxis
                  showYAxis
                />
              </div>
            ) : (
              <div className={styles.chartEmpty}>
                <Activity size={20} />
                <span>Waiting for data…</span>
              </div>
            )}
          </CardBody>
        </Card>

        <Card flat>
          <CardHeader>
            <CardTitle>FPS</CardTitle>
          </CardHeader>
          <CardBody>
            <p className={styles.chartDesc}>Real-time frames per second</p>
            {hasData ? (
              <div className={styles.chartWrap}>
                <Chart
                  type="area"
                  data={chartData}
                  xKey="ts"
                  height={260}
                  series={[{ key: 'fps', label: 'FPS' }]}
                  showGrid
                  showXAxis
                  showYAxis
                />
              </div>
            ) : (
              <div className={styles.chartEmpty}>
                <Activity size={20} />
                <span>Waiting for data…</span>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

export default JobMonitorPanel;
